import { useState, useEffect } from 'react';
import { Button } from 'UI';
import { useTranslation } from 'react-i18next';
import { sanitizeToNumeric, withStyles } from 'vopay';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { useClientData } from '@/providers/ClientData.provider';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import type { TransactionType, UserRoles } from 'vopay';
import useFetchApprovalUsers from '@/hooks/useApi/useFetchApprovalUsers';
import useFetchTransactionTypes from '@/hooks/useApi/useFetchTransactionTypes';
import usePostCreateUserRole from '@/hooks/useApi/usePostCreateUserRole';
import usePostDeleteUserRole from '@/hooks/useApi/usePostDeleteUserRole';
import usePostCreateUserRolePermission from '@/hooks/useApi/usePostCreateUserRolePermission';
import usePostDeleteUserRolePermission from '@/hooks/useApi/usePostDeleteUserRolePermission';
import usePostUpdateUserRolePermission from '@/hooks/useApi/usePostUpdateUserRolePermission';
import ApproverCardEdit, { type ApproverData, type PaymentRailGroup } from './ApproverCardEdit';
import styleSheet from './ApproversEdit.module.scss';

const cn = withStyles(styleSheet);

interface ApproversEditProps {
  approvers: UserRoles[];
  onCancel: () => void;
  onSave: () => Promise<void>;
}

const ApproversEdit = ({ approvers, onCancel, onSave }: ApproversEditProps) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'TransactionApproval.Configuration.Roles.Approvers.Edit',
  });
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const {
    response: { Country },
  } = useClientData();
  const [, setLoading] = useDataFetchIndicator();
  const { response: allUsers, fetchApprovalUsers } = useFetchApprovalUsers();
  const [transactionTypesResponse, fetchTransactionTypes] = useFetchTransactionTypes({});
  const [, , createUserRole] = usePostCreateUserRole({});
  const [, , deleteUserRole] = usePostDeleteUserRole({});
  const [, , createUserRolePermission] = usePostCreateUserRolePermission({});
  const [, , deleteUserRolePermission] = usePostDeleteUserRolePermission({});
  const [, , updateUserRolePermission] = usePostUpdateUserRolePermission({});

  const [currentApprovers, setCurrentApprovers] = useState<ApproverData[]>([]);
  const [originalApprovers, setOriginalApprovers] = useState<ApproverData[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    void fetchApprovalUsers();
    fetchTransactionTypes({
      CountriesID: Country === 'CA' ? 1 : 2,
      IsTransactionApprovalSupported: 1,
    });
  }, []);

  // Convert approvers to grouped format
  useEffect(() => {
    if (approvers && approvers.length > 0) {
      const grouped = groupApproversByUsername(approvers);
      setCurrentApprovers(grouped);
      setOriginalApprovers(JSON.parse(JSON.stringify(grouped))); // Deep copy
    } else {
      // Initialize with one empty approver
      setCurrentApprovers([createEmptyApprover()]);
      setOriginalApprovers([]);
    }
  }, [approvers]);

  const groupApproversByUsername = (userRoles: UserRoles[]): ApproverData[] => {
    const grouped: Record<string, UserRoles[]> = {};

    userRoles.forEach((role) => {
      if (!grouped[role.Username]) {
        grouped[role.Username] = [];
      }
      grouped[role.Username].push(role);
    });

    return Object.entries(grouped).map(([username, roles]) => {
      // Group by amount
      const amountGroups: Record<string, TransactionType[]> = {};
      const hasNoLimitMap: Record<string, boolean> = {};

      roles.forEach((role) => {
        const key = role.MaxAmount || 'NO_LIMIT';
        if (!amountGroups[key]) {
          amountGroups[key] = [];
          hasNoLimitMap[key] = !role.MaxAmount || role.MaxAmount === '';
        }
        amountGroups[key].push(role.TransactionType);
      });

      const paymentRailGroups: PaymentRailGroup[] = Object.entries(amountGroups).map(([amount, rails], index) => ({
        id: `${username}-${index}`,
        paymentRails: rails,
        amount: hasNoLimitMap[amount] ? '' : amount,
        hasNoLimit: hasNoLimitMap[amount],
      }));

      return {
        username,
        paymentRailGroups,
      };
    });
  };

  const createEmptyApprover = (): ApproverData => ({
    username: '',
    paymentRailGroups: [
      {
        id: `new-${Date.now()}`,
        paymentRails: [],
        amount: '',
        hasNoLimit: false,
      },
    ],
  });

  const transactionTypes = transactionTypesResponse?.TransactionTypes || [];

  // Get payment rail options based on Country
  const getPaymentRailOptions = () => {
    const typesList = Object.values(transactionTypes);

    return typesList.map((type) => ({
      value: type.TransactionType,
      label: type.TransactionType,
    }));
  };

  const paymentRailOptions = getPaymentRailOptions();

  // Filter available users (exclude already selected approvers)
  const getAvailableUsers = (excludeUsernames: string[]) => {
    return allUsers.filter((user) => !excludeUsernames.includes(user.Username));
  };

  // Filter available payment rails for a specific group
  const getAvailablePaymentRails = (approverIndex: number, groupIndex: number) => {
    const approver = currentApprovers[approverIndex];
    const selectedRails = approver.paymentRailGroups
      .filter((_, idx) => idx !== groupIndex)
      .flatMap((group) => group.paymentRails);

    return paymentRailOptions.filter((option) => !selectedRails.includes(option.value));
  };

  const handleApproverChange = (index: number, newUsername: string) => {
    const newApprovers = [...currentApprovers];
    newApprovers[index] = { ...newApprovers[index], username: newUsername };
    setCurrentApprovers(newApprovers);
    validateApprover(index, newApprovers[index]);
  };

  const handlePaymentRailsChange = (approverIndex: number, groupIndex: number, newRails: TransactionType[]) => {
    const newApprovers = [...currentApprovers];
    newApprovers[approverIndex].paymentRailGroups[groupIndex].paymentRails = newRails;
    setCurrentApprovers(newApprovers);
    validatePaymentRailGroup(approverIndex, groupIndex, newApprovers[approverIndex].paymentRailGroups[groupIndex]);
  };

  const handleAmountChange = (approverIndex: number, groupIndex: number, newAmount: string) => {
    const newApprovers = [...currentApprovers];
    newApprovers[approverIndex].paymentRailGroups[groupIndex].amount = newAmount;
    setCurrentApprovers(newApprovers);
    validatePaymentRailGroup(approverIndex, groupIndex, newApprovers[approverIndex].paymentRailGroups[groupIndex]);
  };

  const handleNoLimitChange = (approverIndex: number, groupIndex: number, hasNoLimit: boolean) => {
    const newApprovers = [...currentApprovers];
    newApprovers[approverIndex].paymentRailGroups[groupIndex].hasNoLimit = hasNoLimit;
    setCurrentApprovers(newApprovers);
    validatePaymentRailGroup(approverIndex, groupIndex, newApprovers[approverIndex].paymentRailGroups[groupIndex]);
  };

  const handleRemoveApprover = (index: number) => {
    setCurrentApprovers(currentApprovers.filter((_, i) => i !== index));
    // Clear errors for this approver
    const newErrors = { ...errors };
    Object.keys(newErrors).forEach((key) => {
      if (key.startsWith(`approver-${index}-`)) {
        delete newErrors[key];
      }
    });
    setErrors(newErrors);
  };

  const handleAddPaymentRailGroup = (approverIndex: number) => {
    const newApprovers = [...currentApprovers];
    newApprovers[approverIndex].paymentRailGroups.push({
      id: `${newApprovers[approverIndex].username}-${Date.now()}`,
      paymentRails: [],
      amount: '',
      hasNoLimit: false,
    });
    setCurrentApprovers(newApprovers);
  };

  const handleRemovePaymentRailGroup = (approverIndex: number, groupIndex: number) => {
    const newApprovers = [...currentApprovers];
    newApprovers[approverIndex].paymentRailGroups = newApprovers[approverIndex].paymentRailGroups.filter(
      (_, i) => i !== groupIndex,
    );
    setCurrentApprovers(newApprovers);
    // Clear errors for this group
    const errorKey = `approver-${approverIndex}-group-${groupIndex}`;
    const newErrors = { ...errors };
    delete newErrors[`${errorKey}-rails`];
    delete newErrors[`${errorKey}-amount`];
    setErrors(newErrors);
  };

  const handleAddApprover = () => {
    setCurrentApprovers([...currentApprovers, createEmptyApprover()]);
  };

  const validateApprover = (index: number, approver: ApproverData): boolean => {
    const newErrors = { ...errors };
    const errorKey = `approver-${index}-username`;

    if (!approver.username) {
      newErrors[errorKey] = 'Please select a user';
      setErrors(newErrors);
      return false;
    }

    delete newErrors[errorKey];
    setErrors(newErrors);
    return true;
  };

  const validatePaymentRailGroup = (approverIndex: number, groupIndex: number, group: PaymentRailGroup): boolean => {
    const newErrors = { ...errors };
    const railsKey = `approver-${approverIndex}-group-${groupIndex}-rails`;
    const amountKey = `approver-${approverIndex}-group-${groupIndex}-amount`;

    let isValid = true;

    if (group.paymentRails.length === 0) {
      newErrors[railsKey] = 'Please select at least one payment rail';
      isValid = false;
    } else {
      delete newErrors[railsKey];
    }

    if (!group.hasNoLimit && (!group.amount || parseFloat(group.amount) <= 0)) {
      newErrors[amountKey] = 'Amount must be greater than 0';
      isValid = false;
    } else {
      delete newErrors[amountKey];
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateAll = (): boolean => {
    let isValid = true;
    const newErrors: Record<string, string> = {};

    currentApprovers.forEach((approver, approverIndex) => {
      if (!approver.username) {
        newErrors[`approver-${approverIndex}-username`] = 'Please select a user';
        isValid = false;
      }

      approver.paymentRailGroups.forEach((group, groupIndex) => {
        const railsKey = `approver-${approverIndex}-group-${groupIndex}-rails`;
        const amountKey = `approver-${approverIndex}-group-${groupIndex}-amount`;

        if (group.paymentRails.length === 0) {
          newErrors[railsKey] = 'Please select at least one payment rail';
          isValid = false;
        }

        if (!group.hasNoLimit && (!group.amount || parseFloat(group.amount) <= 0)) {
          newErrors[amountKey] = 'Amount must be greater than 0';
          isValid = false;
        }
      });
    });

    setErrors(newErrors);
    return isValid;
  };

  const isFormValid = (): boolean => {
    // Check if there are any errors
    if (Object.keys(errors).length > 0) {
      return false;
    }

    // Check if all approvers have required fields
    return currentApprovers.every((approver) => {
      if (!approver.username) return false;
      return approver.paymentRailGroups.every((group) => {
        if (group.paymentRails.length === 0) return false;
        if (!group.hasNoLimit && (!group.amount || parseFloat(group.amount) <= 0)) return false;
        return true;
      });
    });
  };

  const handleSave = async () => {
    if (!validateAll()) {
      await alertDialog.error(
        t('validationErrorTitle', { defaultValue: 'Validation Error' }),
        t('validationErrorMessage', { defaultValue: 'Please fix all errors before saving.' }),
        {
          abortLabel: t('closeButton', { defaultValue: 'Close' }),
        },
      );
      return;
    }

    setLoading(true);

    try {
      // Process deletions, updates, and creations
      await processApproverChanges();

      toast.success(t('saveSuccessMessage', { defaultValue: 'Approver settings saved' }));
    } catch (error) {
      alertDialog.error(
        t('saveErrorTitle', { defaultValue: 'Error' }),
        t('saveErrorMessage', { defaultValue: 'Some settings have failed to save. Please try again.' }),
        {
          abortLabel: t('closeButton', { defaultValue: 'Close' }),
        },
      );
    } finally {
      setLoading(false);
      onSave();
    }
  };

  const processApproverChanges = async () => {
    // Find approvers that were deleted
    const originalUsernames = originalApprovers.map((a) => a.username);
    const currentUsernames = currentApprovers.filter((a) => a.username).map((a) => a.username);
    const deletedUsernames = originalUsernames.filter((u) => !currentUsernames.includes(u));

    // Track if any operation failed
    let hasError = false;

    // Delete removed approvers
    for (const username of deletedUsernames) {
      // Find the original approver to get their permissions
      const originalApprover = originalApprovers.find((a) => a.username === username);

      if (originalApprover) {
        // Delete all permissions first
        for (const group of originalApprover.paymentRailGroups) {
          for (const rail of group.paymentRails) {
            const deletePermResult = await deleteUserRolePermission({
              Username: username,
              Role: 'approver',
              TransactionType: rail,
            });
            if (!deletePermResult.Success) {
              hasError = true;
              console.error(`Couldn't delete user role permission: ${deletePermResult.ErrorMessage}`);
            }
          }
        }
      }

      // Then delete the role
      const result = await deleteUserRole({ Username: username, Role: 'approver' });
      if (!result.Success) {
        hasError = true;
        console.error(`Couldn't delete user role: ${result.ErrorMessage}`);
      }
    }

    // Process each current approver
    for (const approver of currentApprovers) {
      if (!approver.username) continue;

      const originalApprover = originalApprovers.find((a) => a.username === approver.username);

      if (!originalApprover) {
        // New approver - create role and all permissions
        const createRoleResult = await createUserRole({ Username: approver.username, Role: 'approver' });
        if (!createRoleResult.Success) {
          hasError = true;
          console.error(`Couldn't create user role: ${createRoleResult.ErrorMessage}`);
          continue; // Skip creating permissions if role creation failed
        }

        for (const group of approver.paymentRailGroups) {
          for (const rail of group.paymentRails) {
            const createPermResult = await createUserRolePermission({
              Username: approver.username,
              Role: 'approver',
              TransactionType: rail,
              MaxAmount: group.hasNoLimit ? 1 : Number(sanitizeToNumeric(group.amount)),
              SetNoMaxAmount: group.hasNoLimit,
            });
            if (!createPermResult.Success) {
              console.error(`Couldn't create user role permission: ${createPermResult.ErrorMessage}`);
              hasError = true;
            }
          }
        }
      } else {
        // Existing approver - update permissions
        const updateError = await updateApproverPermissions(approver, originalApprover);
        if (updateError) {
          //don't need log here handled in updateApproverPermissions
          hasError = true;
        }
      }
    }

    // Throw error if any operation failed
    if (hasError) {
      throw new Error('One or more operations failed');
    }
  };

  const updateApproverPermissions = async (current: ApproverData, original: ApproverData): Promise<boolean> => {
    let hasError = false;

    // Create a map of original permissions: rail -> amount
    const originalPermissions = new Map<string, { amount: string; hasNoLimit: boolean }>();
    original.paymentRailGroups.forEach((group) => {
      group.paymentRails.forEach((rail) => {
        originalPermissions.set(rail, { amount: group.amount, hasNoLimit: group.hasNoLimit });
      });
    });

    // Create a map of current permissions: rail -> amount
    const currentPermissions = new Map<string, { amount: string; hasNoLimit: boolean }>();
    current.paymentRailGroups.forEach((group) => {
      group.paymentRails.forEach((rail) => {
        currentPermissions.set(rail, { amount: group.amount, hasNoLimit: group.hasNoLimit });
      });
    });

    // Find rails to delete
    for (const [rail, _] of originalPermissions) {
      if (!currentPermissions.has(rail)) {
        const result = await deleteUserRolePermission({
          Username: current.username,
          Role: 'approver',
          TransactionType: rail,
        });
        if (!result.Success) {
          console.error(`Couldn't delete user role permission: ${result.ErrorMessage}`);
          hasError = true;
        }
      }
    }

    // Find rails to create or update
    for (const [rail, config] of currentPermissions) {
      const originalConfig = originalPermissions.get(rail);

      if (!originalConfig) {
        // Create new permission
        const result = await createUserRolePermission({
          Username: current.username,
          Role: 'approver',
          TransactionType: rail,
          MaxAmount: config.hasNoLimit ? 1 : Number(sanitizeToNumeric(config.amount)),
          SetNoMaxAmount: config.hasNoLimit,
        });
        if (!result.Success) {
          console.error(`Couldn't create user role permission: ${result.ErrorMessage}`);
          hasError = true;
        }
      } else if (originalConfig.amount !== config.amount || originalConfig.hasNoLimit !== config.hasNoLimit) {
        // Update existing permission
        const result = await updateUserRolePermission({
          Username: current.username,
          Role: 'approver',
          TransactionType: rail,
          MaxAmount: config.hasNoLimit ? 1 : Number(sanitizeToNumeric(config.amount)),
          SetNoMaxAmount: config.hasNoLimit,
        });
        if (!result.Success) {
          console.error(`Couldn't update user role permission: ${result.ErrorMessage}`);
          hasError = true;
        }
      }
    }

    return hasError;
  };

  return (
    <div className={cn('container')}>
      <div className={cn('instructions')}>
        {t('instructions', {
          defaultValue:
            'These users approve transactions depending on their assigned payment rails and maximum approval amount. Each maximum approval amount is tied to a payment rail.',
        })}
      </div>

      <div className={cn('approversList')}>
        {currentApprovers.map((approver, approverIndex) => {
          const otherUsernames = currentApprovers
            .filter((_, i) => i !== approverIndex)
            .map((a) => a.username)
            .filter(Boolean);
          const availableUsers = getAvailableUsers(otherUsernames);

          return (
            <ApproverCardEdit
              key={approverIndex}
              approver={approver}
              approverIndex={approverIndex}
              availableUsers={availableUsers.map((user) => ({
                label: user.Username,
                value: user.Username,
              }))}
              paymentRailOptions={paymentRailOptions}
              errors={errors}
              onApproverChange={(username) => handleApproverChange(approverIndex, username)}
              onPaymentRailsChange={(groupIndex, rails) => handlePaymentRailsChange(approverIndex, groupIndex, rails)}
              onAmountChange={(groupIndex, amount) => handleAmountChange(approverIndex, groupIndex, amount)}
              onNoLimitChange={(groupIndex, hasNoLimit) => handleNoLimitChange(approverIndex, groupIndex, hasNoLimit)}
              onRemove={() => handleRemoveApprover(approverIndex)}
              onAddPaymentRailGroup={() => handleAddPaymentRailGroup(approverIndex)}
              onRemovePaymentRailGroup={(groupIndex) => handleRemovePaymentRailGroup(approverIndex, groupIndex)}
              getAvailablePaymentRails={(groupIndex) => getAvailablePaymentRails(approverIndex, groupIndex)}
            />
          );
        })}
      </div>

      <div className={cn('actions')}>
        <Button onClick={handleAddApprover} size="small" iconLeft="add">
          {t('addApproverButton', { defaultValue: 'Additional Approver' })}
        </Button>
        <div className={cn('actionButtons')}>
          <Button onClick={onCancel} size="small">
            {t('cancelButton', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={handleSave} size="small" colorScheme="primary" disabled={!isFormValid()}>
            {t('saveButton', { defaultValue: 'Save' })}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ApproversEdit;
